import React, { useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { rowKey, withRowKeys } from "@/lib/rowKey";
import { TemplateList } from "@/components/settings/TemplateList";
import { TemplateForm } from "@/components/settings/TemplateForm";

const emptyTemplate = {
  name: "",
  edit_quota: 10,
  allow_extra_edits: true,
  max_extra_edits: 20,
  extra_edit_price: "",
  package_price: "",
  print_sizes: "",
  print_quota_items: [{ size: "", qty: 1, _k: rowKey() }],
};

const toForm = (tpl) => ({
  name: tpl.name,
  edit_quota: tpl.edit_quota,
  allow_extra_edits: tpl.allow_extra_edits,
  max_extra_edits: tpl.max_extra_edits,
  extra_edit_price: tpl.extra_edit_price ?? "",
  package_price: tpl.package_price ?? "",
  print_sizes: (tpl.print_sizes || []).join(", "),
  print_quota_items: (tpl.print_quota_items || []).length
    ? withRowKeys(tpl.print_quota_items.map((i) => ({ size: i.size, qty: i.qty })))
    : [{ size: "", qty: 1, _k: rowKey() }],
});

/** Studio-owned project presets, managed from Settings and applied on the create screen. */
export const TemplateManager = ({ templates, onChange }) => {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyTemplate);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const payload = () => ({
    name: form.name,
    edit_quota: Number(form.edit_quota),
    allow_extra_edits: form.allow_extra_edits,
    max_extra_edits: Number(form.max_extra_edits),
    extra_edit_price: form.extra_edit_price ? Number(form.extra_edit_price) : null,
    package_price: form.package_price ? Number(form.package_price) : null,
    print_sizes: form.print_sizes.split(",").map((s) => s.trim()).filter(Boolean),
    print_quota_items: form.print_quota_items
      .filter((i) => i.size.trim())
      .map((i) => ({ size: i.size, qty: Number(i.qty), frame_required: false })),
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) await api.put(`/studio/templates/${editing}`, payload());
      else await api.post("/studio/templates", payload());
      setForm(emptyTemplate);
      setEditing(null);
      await onChange();
      toast.success(t("template_saved"));
    } catch (err) {
      toast.error(apiError(err, "Could not save template"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/studio/templates/${id}`);
      if (editing === id) { setEditing(null); setForm(emptyTemplate); }
      await onChange();
    } catch (err) {
      toast.error(apiError(err, "Could not delete template"));
    }
  };

  const setQuota = (idx, patch) =>
    setForm((f) => ({
      ...f,
      print_quota_items: f.print_quota_items.map((i, n) => (n === idx ? { ...i, ...patch } : i)),
    }));

  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-7 space-y-7" data-testid="templates-panel">
      <div>
        <p className="eyebrow">{t("templates")}</p>
        <p className="mt-3 text-[11px] text-[#707070] leading-relaxed max-w-xl">{t("templates_hint")}</p>
      </div>

      <TemplateList
        templates={templates}
        onEdit={(tpl) => { setEditing(tpl.template_id); setForm(toForm(tpl)); }}
        onRemove={remove}
      />

      <TemplateForm
        form={form}
        setForm={setForm}
        editing={editing}
        busy={busy}
        onSubmit={submit}
        onCancel={() => { setEditing(null); setForm(emptyTemplate); }}
        onQuota={setQuota}
      />
    </div>
  );
};
